import type {
  ArchimedesSolution,
  ArchimedesTarget,
  FloatSolution,
} from "@/lib/tool-kit/physics-fields/buoyancy";
import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";

/** Equation, substitution and answer in SI. Symbols are not translated. */

export function archimedesWorking(
  target: ArchimedesTarget,
  s: ArchimedesSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  if (target === "volume") {
    return [
      "V = F_b / (ρ g)",
      `V = ${e(s.force)} / (${e(s.density)} × ${e(s.gravity)}) = ${r(s.volume, "m³")}`,
    ];
  }
  if (target === "density") {
    return [
      "ρ = F_b / (V g)",
      `ρ = ${e(s.force)} / (${e(s.volume)} × ${e(s.gravity)}) = ${r(s.density, "kg/m³")}`,
    ];
  }
  return [
    "F_b = ρ V g",
    `F_b = ${e(s.density)} × ${e(s.volume)} × ${e(s.gravity)} = ${r(s.force, "N")}`,
  ];
}

export function floatWorking(
  s: FloatSolution,
  fromMass: boolean,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const lines: string[] = [];
  if (fromMass) {
    lines.push(
      `ρ_object = m / V = ${e(s.mass)} / ${e(s.volume)} = ${r(s.objectDensity, "kg/m³")}`
    );
  }
  lines.push(
    `ρ_object / ρ_fluid = ${e(s.objectDensity)} / ${e(s.fluidDensity)} = ${formatSig(fmt, s.objectDensity / s.fluidDensity, Math.max(sig, 4))}`
  );
  if (s.state === "sinks") {
    lines.push(
      `F_b = ρ_fluid V g = ${e(s.fluidDensity)} × ${e(s.volume)} × ${e(s.gravity)} = ${r(s.maxBuoyancy, "N")}`,
      `W = m g = ${e(s.mass)} × ${e(s.gravity)} = ${r(s.weight, "N")}`,
      `W − F_b = ${r(s.apparentWeightSubmerged, "N")}`
    );
  } else {
    lines.push(
      `submerged fraction = ρ_object / ρ_fluid = ${r(s.fractionSubmerged * 100, "%")}`,
      `F_b = W = m g = ${e(s.mass)} × ${e(s.gravity)} = ${r(s.weight, "N")}`,
      `extra load before sinking = ρ_fluid V g − W = ${r(s.reserve, "N")}`
    );
  }
  return lines;
}
